import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import React, { useCallback, useMemo, useState } from 'react';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { DataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { DataSetVersionSelectorModal } from 'apps/sigma2-ui/src/data-wizard/components/DataSetVersionSelectorModal';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { getTimestampAsReadableString } from 'libs/end-to-end/src/dates';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSetVersionsFull } from 'libs/end-to-end/src/types/databases/data-sets/data-set-version';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { DataSetVersionFileSelector } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetVersionFileSelector';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';

export interface TransformDataSetSelectorModalTaskNone {
  type: 'none';
}

export interface TransformDataSetSelectorModalTaskAddNew {
  type: 'add-new';
}

export interface TransformDataSetSelectorModalTaskEdit {
  type: 'edit';
  oldDataSetReference: DataSetReference;
}

export type TransformDataSetSelectorModalTask =
  | TransformDataSetSelectorModalTaskNone
  | TransformDataSetSelectorModalTaskAddNew
  | TransformDataSetSelectorModalTaskEdit;

export interface TransformDataSetSelectorHook {
  addDataSetVersionButtonLabel: string;
  addDataSetVersionButtonBehavior: TouchTargetBehavior;
  addDataSetVersionButtonDisabled: boolean;
  backButtonLabel: string;
  backButtonDisabled: boolean;
  backButtonBehavior: TouchTargetBehavior;
  modal: React.ReactNode;
  heading: string[];
  rows: AdminTableRow[];
}

export const useTransformDataSetSelector = ({
  organizationId,
  databaseId,
  taggedDataSetReferences,
  setTaggedDataSetReferences,
  isRunningTransform,
}: {
  organizationId: string;
  databaseId: string;
  taggedDataSetReferences: TaggedDataSetReference[];
  setTaggedDataSetReferences: Setter<TaggedDataSetReference[]>;
  isRunningTransform: boolean;
}): TransformDataSetSelectorHook => {
  const listDataSetVersionsFullPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-set-versions-full`;
  }, [organizationId, databaseId]);

  const listDataSetVersionsFullPattern = useSimpleReadPattern({
    getUrlFn: listDataSetVersionsFullPatternGetUrlFn,
    canRead: true,
    responseSchema: DataSetVersionsFull,
  });

  const fullVersionsResponse: DataSetVersionsFull = useMemo(() => {
    return listDataSetVersionsFullPattern.status === 'done'
      ? listDataSetVersionsFullPattern.entity
      : {
          fullVersions: [],
        };
  }, [listDataSetVersionsFullPattern]);

  const navigate = useNavigateSafe();
  const language = useLanguage();

  const [modalTask, setModalTask] = useState<TransformDataSetSelectorModalTask>(
    {
      type: 'none',
    },
  );

  const onReplaceWithLatest = useCallback(
    (dataSetId: string) => {
      setTaggedDataSetReferences([
        ...taggedDataSetReferences.map((reference) => {
          if (reference.dataSetReference.dataSetId !== dataSetId) {
            return reference;
          }

          const latestReference = fullVersionsResponse.fullVersions
            .sort((a, b) => {
              return b.dataSetVersion.updatedAt - a.dataSetVersion.updatedAt;
            })
            .find((fullVersion) => fullVersion.dataSet.id === dataSetId);

          if (!latestReference) {
            return reference;
          }

          return {
            ...reference,
            dataSetReference: {
              dataSetId: latestReference.dataSet.id,
              dataSetVersion: latestReference.dataSetVersion.dataSetVersion,
            },
          };
        }),
      ]);
    },
    [fullVersionsResponse, setTaggedDataSetReferences, taggedDataSetReferences],
  );

  const closeModal = useCallback(() => {
    setModalTask({
      type: 'none',
    });
  }, [setModalTask]);

  const modalOnComplete = useCallback(
    (dataSetReference: DataSetReference) => {
      if (modalTask.type === 'none') {
        return;
      }

      if (modalTask.type === 'add-new') {
        setTaggedDataSetReferences([
          ...taggedDataSetReferences,
          {
            dataSetReference,
            automationTag: '',
          },
        ]);

        closeModal();
        return;
      }

      if (modalTask.type === 'edit') {
        const newReferences = taggedDataSetReferences.map((reference) => {
          if (
            reference.dataSetReference.dataSetVersion ===
            modalTask.oldDataSetReference.dataSetVersion
          ) {
            return {
              dataSetReference,
              automationTag: reference.automationTag,
            };
          }
          return reference;
        });

        setTaggedDataSetReferences(newReferences);

        closeModal();
        return;
      }

      throw guardExhaustive(modalTask);
    },
    [
      modalTask,
      closeModal,
      setTaggedDataSetReferences,
      taggedDataSetReferences,
    ],
  );

  return useMemo(() => {
    return {
      addDataSetVersionButtonLabel: 'Add Data Set',
      addDataSetVersionButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setModalTask({
            type: 'add-new',
          });
        },
      },
      addDataSetVersionButtonDisabled: isRunningTransform,
      backButtonLabel: 'Back',
      backButtonDisabled: isRunningTransform,
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          );
        },
      },
      heading: [
        'Data Set Version',
        'Automation Tag',
        'Edit',
        'Remove',
        'File Information',
      ],
      rows: taggedDataSetReferences.map((taggedDataSetReference) => {
        const fullVersion = fullVersionsResponse.fullVersions.find(
          (version) =>
            version.dataSetVersion.dataSetVersion ===
            taggedDataSetReference.dataSetReference.dataSetVersion,
        );

        const fileConsoleUrls = fullVersion?.fileConsoleUrls ?? {};

        const totalNumberOfFiles = fullVersion?.totalNumberOfFiles ?? 0;

        return {
          key: taggedDataSetReference.dataSetReference.dataSetVersion,
          columns: [
            {
              key: 'dataSetName',
              columnType: 'custom',
              ui: (
                <Spacing size={'sm'} direction={'vertical'}>
                  <S2Typography
                    variant={'headline-5-alt'}
                    translatedContent={language.dangerousUserInput({
                      value: fullVersion?.dataSet.displayName ?? '',
                    })}
                  />

                  <S2Typography
                    variant={'body-6'}
                    translatedContent={language.dangerousUserInput({
                      value: fullVersion
                        ? getTimestampAsReadableString(
                            fullVersion.dataSetVersion.createdAt,
                          )
                        : '',
                    })}
                  />

                  <S2Typography
                    variant={'body-6'}
                    translatedContent={language.dangerousUserInput({
                      value: fullVersion?.dataSet.id ?? '',
                    })}
                    dangerouslyOverrideClassNameFn={(classNames) =>
                      `${classNames} h-[72px]`
                    }
                  />

                  {fullVersion && fullVersion.dataSet.isArchived && (
                    <S2Typography
                      variant={'body-6'}
                      translatedContent={language.dangerousUserInput({
                        value: 'This data set is archived.',
                      })}
                      textColorVariant={'red-600'}
                    />
                  )}
                </Spacing>
              ),
            },
            {
              key: 'automationTag',
              columnType: 'custom',
              ui: (
                <SimpleInput
                  label={''}
                  value={taggedDataSetReference.automationTag}
                  disabled={isRunningTransform}
                  onChange={(newTag) => {
                    setTaggedDataSetReferences(
                      taggedDataSetReferences.map((reference) => {
                        if (
                          reference.dataSetReference.dataSetVersion ===
                          taggedDataSetReference.dataSetReference.dataSetVersion
                        ) {
                          return {
                            dataSetReference: reference.dataSetReference,
                            automationTag: newTag,
                          };
                        }
                        return reference;
                      }),
                    );
                  }}
                />
              ),
            },
            {
              key: 'editButton',
              columnType: 'custom',
              ui: (
                <Spacing size={'sm'} direction={'vertical'}>
                  <Button
                    size={'small'}
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        setModalTask({
                          type: 'edit',
                          oldDataSetReference:
                            taggedDataSetReference.dataSetReference,
                        });
                      },
                    }}
                    text={'Edit'}
                    disabled={isRunningTransform}
                  />

                  <Button
                    size={'small'}
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        onReplaceWithLatest(
                          taggedDataSetReference.dataSetReference.dataSetId,
                        );
                      },
                    }}
                    text={'Replace w/ Latest'}
                    disabled={isRunningTransform}
                  />
                </Spacing>
              ),
            },
            {
              key: 'removeButton',
              columnType: 'custom',
              ui: (
                <Button
                  size={'small'}
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      setTaggedDataSetReferences(
                        taggedDataSetReferences.filter(
                          (reference) =>
                            reference.dataSetReference.dataSetVersion !==
                            taggedDataSetReference.dataSetReference
                              .dataSetVersion,
                        ),
                      );
                    },
                  }}
                  text={'Remove'}
                  disabled={isRunningTransform}
                />
              ),
            },
            {
              key: 'fileInformation',
              columnType: 'custom',
              ui: (
                <DataSetVersionFileSelector
                  organizationId={organizationId}
                  databaseId={databaseId}
                  dataSetId={taggedDataSetReference.dataSetReference.dataSetId}
                  dataSetVersion={
                    taggedDataSetReference.dataSetReference.dataSetVersion
                  }
                  automationTag={taggedDataSetReference.automationTag}
                  initialFileInfo={{
                    fileConsoleUrls: fileConsoleUrls,
                    totalNumberOfFiles: totalNumberOfFiles,
                  }}
                />
              ),
            },
          ],
        };
      }),
      modal:
        modalTask.type === 'none' ? null : (
          <DataSetVersionSelectorModal
            dataSetReference={
              modalTask.type === 'add-new'
                ? null
                : modalTask.oldDataSetReference
            }
            onComplete={modalOnComplete}
            onClose={closeModal}
            organizationId={organizationId}
            fullVersions={fullVersionsResponse.fullVersions}
          />
        ),
    };
  }, [
    isRunningTransform,
    taggedDataSetReferences,
    modalTask,
    modalOnComplete,
    closeModal,
    organizationId,
    fullVersionsResponse,
    navigate,
    databaseId,
    language,
    setTaggedDataSetReferences,
    onReplaceWithLatest,
  ]);
};
